import queryString from 'query-string';

import omit from 'lodash/omit';

import appConfig from '../../appConfig';
import {
  QUERY_STRING_PREFIX,
  ABSOLUTE_URL_REGEX,
} from './urlConstants';
import {PAGINATION_PARAM_NAMES} from '../pagination/paginationConstants';
import {
  filterNonExistentObjectValues,
} from '../collection/collectionUtils';
import {HTTP_STATUS_CODES} from '../network/httpConstants';
import {FACET_QUERY_PARAM_NAMES} from '../../views/filter/facet/facetConstants';

/* eslint-disable no-param-reassign */
const URLParser = URL;

const testParameter = (name, filters) => {
  return filters.some(filter => filter instanceof RegExp ? filter.test(name) : filter === name);
};

function normalizeUrl(urlString, options) {
  options = {
    defaultProtocol: 'http:',
    normalizeProtocol: true,
    forceHttp: false,
    forceHttps: false,
    stripAuthentication: true,
    stripHash: false,
    stripWWW: true,
    removeQueryParameters: [/^utm_\w+/i],
    removeTrailingSlash: true,
    removeDirectoryIndex: false,
    sortQueryParameters: true,
    ...options,
  };

  // TODO: Remove this at some point in the future
  if (Reflect.has(options, 'normalizeHttps')) {
    throw new Error('options.normalizeHttps is renamed to options.forceHttp');
  }

  if (Reflect.has(options, 'normalizeHttp')) {
    throw new Error('options.normalizeHttp is renamed to options.forceHttps');
  }

  if (Reflect.has(options, 'stripFragment')) {
    throw new Error('options.stripFragment is renamed to options.stripHash');
  }

  urlString = urlString.trim();

  const hasRelativeProtocol = urlString.startsWith('//');
  const isRelativeUrl = !hasRelativeProtocol && /^\.*\//.test(urlString);

  // Prepend protocol
  if (!isRelativeUrl) {
    urlString = urlString.replace(/^(?!(?:\w+:)?\/\/)|^\/\//, options.defaultProtocol);
  }

  const urlObj = new URLParser(urlString);

  if (options.forceHttp && options.forceHttps) {
    throw new Error('The `forceHttp` and `forceHttps` options cannot be used together');
  }

  if (options.forceHttp && urlObj.protocol === 'https:') {
    urlObj.protocol = 'http:';
  }

  if (options.forceHttps && urlObj.protocol === 'http:') {
    urlObj.protocol = 'https:';
  }

  // Remove auth
  if (options.stripAuthentication) {
    urlObj.username = '';
    urlObj.password = '';
  }

  // Remove hash
  if (options.stripHash) {
    urlObj.hash = '';
  }

  // Remove duplicate slashes if not preceded by a protocol
  if (urlObj.pathname) {
    // TODO: Use the following instead when targeting Node.js 10
    // `urlObj.pathname = urlObj.pathname.replace(/(?<!https?:)\/{2,}/g, '/');`
    urlObj.pathname = urlObj.pathname.replace(/((?![https?:]).)\/{2,}/g, (_, p1) => {
      if (/^(?!\/)/g.test(p1)) {
        return `${p1}/`;
      }
      return '/';
    });
  }

  // Decode URI octets
  if (urlObj.pathname) {
    urlObj.pathname = decodeURI(urlObj.pathname);
  }

  // Remove directory index
  if (options.removeDirectoryIndex === true) {
    options.removeDirectoryIndex = [/^index\.[a-z]+$/];
  }

  if (Array.isArray(options.removeDirectoryIndex) && options.removeDirectoryIndex.length > 0) {
    let pathComponents = urlObj.pathname.split('/');
    const lastComponent = pathComponents[pathComponents.length - 1];

    if (testParameter(lastComponent, options.removeDirectoryIndex)) {
      pathComponents = pathComponents.slice(0, pathComponents.length - 1);
      urlObj.pathname = pathComponents.slice(1).join('/') + '/';
    }
  }

  if (urlObj.hostname) {
    // Remove trailing dot
    urlObj.hostname = urlObj.hostname.replace(/\.$/, '');

    // Remove `www.`
    if (options.stripWWW && /^www\.([a-z\-\d]{2,63})\.([a-z.]{2,5})$/.test(urlObj.hostname)) {
      // Each label should be max 63 at length (min: 2).
      // The extension should be max 5 at length (min: 2).
      // Source: https://en.wikipedia.org/wiki/Hostname#Restrictions_on_valid_host_names
      urlObj.hostname = urlObj.hostname.replace(/^www\./, '');
    }
  }

  // Remove query unwanted parameters
  if (Array.isArray(options.removeQueryParameters)) {
    for (const key of [...urlObj.searchParams.keys()]) {
      if (testParameter(key, options.removeQueryParameters)) {
        urlObj.searchParams.delete(key);
      }
    }
  }

  // Sort query parameters
  if (options.sortQueryParameters) {
    sortProperties(urlObj.searchParams);
  }

  // Take advantage of many of the Node `url` normalizations
  urlString = urlObj.toString();

  // Remove ending `/`
  if (options.removeTrailingSlash || urlObj.pathname === '/') {
    urlString = urlString.replace(/\/$/, '');
  }

  // Restore relative protocol, if applicable
  if (hasRelativeProtocol && !options.normalizeProtocol) {
    urlString = urlString.replace(/^http:\/\//, '//');
  }

  // Remove http/https
  if (options.stripProtocol) {
    urlString = urlString.replace(/^(?:https?:)?\/\//, '');
  }

  return urlString;
}

function generateQueryString(params,
                             {
                               withPrefix = true,
                               replaceSpaces = false,
                               ...queryStringLibOptions
                             } = {}) {
  let result = queryString.stringify(
    filterNonExistentObjectValues(
      params,
      {
        shouldFilterEmptyStrings: true,
      },
    ),
    queryStringLibOptions,
  );

  if (result && withPrefix) {
    result = QUERY_STRING_PREFIX + result;
  }
  if (replaceSpaces) {
    result = result?.replace(/%20/g, '+');
  }

  return result;
}

/* eslint-disable */
function sortProperties(obj) {
  const sortable = [];
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      sortable.push([key, obj[key]]);
    }
  }
  sortable.sort(function(a, b) {
    const x = a[1].toLowerCase(),
      y = b[1].toLowerCase();
    return x < y ? -1 : x > y ? 1 : 0;
  });
  return sortable;
}
/* eslint-enable */

function generateReferrerUrl(targetUrl, referrerUrl) {
  return `${targetUrl}?ReturnUrl=${encodeURIComponent(referrerUrl)}`;
}

function generateSearchUrl(queryString, queryParams) {
  return queryString ?
    `${PAGINATION_PARAM_NAMES.QUERY}=${encodeURIComponent(queryParams[PAGINATION_PARAM_NAMES.QUERY]).replace(/%20/g, '+')}&${queryString}` :
    `${PAGINATION_PARAM_NAMES.QUERY}=${encodeURIComponent(queryParams[PAGINATION_PARAM_NAMES.QUERY]).replace(/%20/g, '+')}`;
}

function generateSearchbarUrl(queryString, query) {
  return `${PAGINATION_PARAM_NAMES.QUERY}=${encodeURIComponent(query).replace(/%20/g, '+')}`;
}

/* eslint-enable no-param-reassign */

function extractQueryParams(string) {
  return queryString.parse(string);
}

function extractQueryParamPairStrings(urlString) {
  let queryParamsString;
  const urlStringPartials = urlString.split(QUERY_STRING_PREFIX);

  if (urlStringPartials.length === 1) {
    queryParamsString = urlString;
  } else {
    queryParamsString = urlStringPartials[1];
  }

  return queryParamsString.split(/&&|&/gi);
}

function removeQueryStringFromUrl(url) {
  const partials = url.split(QUERY_STRING_PREFIX);

  return partials[0];
}

function removeAnyQueryStringValueFromUrl(url, parameter) {
  const urlparts = url.split('?');

  if (urlparts.length >= 2) {
    const prefix = encodeURIComponent(parameter) + '=';
    const pars = urlparts[1].split(/[&;]/g);

    for (let i = pars.length; i-- > 0;) {
      if (pars[i].lastIndexOf(prefix, 0) !== -1) {
        pars.splice(i, 1);
      }
    }

    return urlparts[0] + (pars.length > 0 ? '?' + pars.join('&') : '');
  }

  return url;
}

function generateNormalizedUrl(url,
                               options = {
                                 stripWWW: false,
                               }) {
  return normalizeUrl(url, options);
}

function isAbsoluteUrl(url) {
  if (typeof url !== 'string') {
    throw new TypeError('Expected a string');
  }

  return ABSOLUTE_URL_REGEX.test(url);
}

function isExternalUrl(url) {
  let result;

  if (isAbsoluteUrl(url)) {
    if (url.startsWith('mailto')) {
      result = true;
    } else {
      result = Boolean(generateNormalizedUrl(url).startsWith(appConfig.sfBaseUrl));
    }
  } else {
    result = false;
  }

  return result;
}

function getHashValues(hashedString) {
  let result = {};
  const splitHash = hashedString.slice(hashedString.indexOf('#') + 1).split('|');

  splitHash.forEach(part => {
    let values = part.split('=');
    result[values[0]] = values[1];
  });

  return result;
}

function removeHashes() {
  let scrollV, scrollH, loc = window.location;

  if ('pushState' in history) {
    history.pushState('', document.title, loc.pathname + loc.search);
  } else {
    scrollV = document.body.scrollTop;
    scrollH = document.body.scrollLeft;

    loc.hash = '';

    document.body.scrollTop = scrollV;
    document.body.scrollLeft = scrollH;
  }
}

function removeParams(sParam) {
  let url = location.search.split('?')[0] + '?';
  let sPageURL = decodeURIComponent(window.location.search.substring(1)),
    sURLVariables = sPageURL.split('&'),
    sParameterName,
    i;

  for (i = 0; i < sURLVariables.length; i++) {
    sParameterName = sURLVariables[i].split('=');
    if (sParameterName[0] !== sParam) {
      url = url + sParameterName[0] + '=' + sParameterName[1] + '&'
    }
  }
  return url.substring(0, url.length - 1);
}

function redirectAsynchronouslyDelivery(routeContext, fetchOperationContext, queryParams) {
  return new Promise((resolve, reject) => {
    fetchOperationContext.redirection = {
      type: HTTP_STATUS_CODES.FOUND,
      url: removeQueryStringFromUrl(routeContext.pathname) +
          generateQueryString({
            [PAGINATION_PARAM_NAMES.QUERY]: queryParams[PAGINATION_PARAM_NAMES.QUERY] || null,
            ...queryParams,
            [PAGINATION_PARAM_NAMES.PAGE]: null,
          }, {
            sort: false, encode: false, withPrefix: true
          }),
    };
    resolve(fetchOperationContext);
  });
}

const getHurlyUrlWhenSortingIsHardcoded = ({hurlyUrlForParams, sortItemKey}) => {
  const HURLY_BLACKLIST_KEYS = [
    'cityCode',
    'cityName',
    'cities',
    'name',
    'receiverCity',
    'receiverTown',
    'receiverDistrict',
    'receiverTownCode',
    'isBot'
  ];
  const path = hurlyUrlForParams?.split('?')?.[0];
  const {query} = queryString.parseUrl(hurlyUrlForParams);
  const clearedUrlParams = omit(query, HURLY_BLACKLIST_KEYS);

  return queryString.stringifyUrl(
    {
      url: path,
      query: {
        ...clearedUrlParams,
        [FACET_QUERY_PARAM_NAMES.SORT]: sortItemKey
      }
    },
    {sort: false, encode: false}
  );
};

export {
  URLParser,
  extractQueryParams,
  generateReferrerUrl,
  generateSearchUrl,
  generateQueryString,
  extractQueryParamPairStrings,
  removeQueryStringFromUrl,
  removeAnyQueryStringValueFromUrl,
  isExternalUrl,
  isAbsoluteUrl,
  generateNormalizedUrl,
  getHashValues,
  removeHashes,
  generateSearchbarUrl,
  removeParams,
  redirectAsynchronouslyDelivery,
  getHurlyUrlWhenSortingIsHardcoded
};
